/**
 * KB-META
 * Purpose: Dev-only detection of runaway HTTP request loops
 * Role: ui.service
 *
 * Security: DO NOT log query strings (may contain PII like emails).
 */
import { Injectable } from '@angular/core';
import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

type WindowStats = {
  startTs: number;
  counts: Map<string, number>;
  warned: Set<string>;
};

@Injectable()
export class RunawayApiMonitorInterceptor implements HttpInterceptor {
  private readonly windowMs = 60_000; // 60s rolling window
  private readonly warnThreshold = 40; // warn if an endpoint exceeds this in one window
  private readonly state: WindowStats = {
    startTs: Date.now(),
    counts: new Map(),
    warned: new Set()
  };

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      // Skip OPTIONS preflight and avoid noise from logger endpoints.
      if (req.method === 'OPTIONS') {
        return next.handle(req);
      }

      const endpoint = this.stripQuery(req.url);
      // Skip logger endpoints to avoid alert spam.
      if (/\/(dev|prod)\/logs?$|\/logs?$/i.test(endpoint)) {
        return next.handle(req);
      }

      const now = Date.now();
      if (now - this.state.startTs > this.windowMs) {
        this.state.startTs = now;
        this.state.counts.clear();
        this.state.warned.clear();
      }

      const key = `${req.method} ${endpoint}`;
      const nextCount = (this.state.counts.get(key) ?? 0) + 1;
      this.state.counts.set(key, nextCount);

      if (nextCount > this.warnThreshold && !this.state.warned.has(key)) {
        this.state.warned.add(key);
        // Intentionally omit query strings to avoid PII (emails, GUIDs in params, etc.)
        const seconds = Math.max(1, Math.round((now - this.state.startTs) / 1000));
        // eslint-disable-next-line no-console
        console.warn(
          `[Runaway API monitor] High request rate: ${key} hit ${nextCount} requests in ~${seconds}s. ` +
            `This may indicate a UI loop (valueChanges/hover/polling) without debounce/cancel/dedupe.`
        );
      }
    } catch {
      // Never break requests because of monitoring.
    }

    return next.handle(req);
  }

  private stripQuery(url: string): string {
    try {
      const u = new URL(url, window.location.origin);
      return u.pathname;
    } catch {
      return String(url || '').split('?')[0];
    }
  }
}

